import React, { useState } from 'react';
import { customerService, orderService, repairService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassPanel, GlassButton, GlassInput, GlassSelect, GlassCard } from '../components/GlassUI';
import PaymentsPanel from '../components/PaymentsPanel';
import { FaRupeeSign, FaCheck, FaPercent, FaWeight, FaHammer, FaWrench, FaBoxOpen, FaUser, FaWhatsapp, FaGem, FaStar, FaWallet, FaPlus, FaTrash, FaEdit, FaHistory } from 'react-icons/fa';
import QrCode from '../components/QrCode';

interface LocalPayment {
  amount: string;
  kind: string; // ADVANCE | PAYMENT | FINAL
  status: string; // PAID | UNPAID
  method: string;
  paidAt: string;
  note: string;
}

const todayInput = () => new Date().toISOString().slice(0, 10);

const emptyPayment = (): LocalPayment => ({
  amount: '',
  kind: 'ADVANCE',
  status: 'PAID',
  method: 'CASH',
  paidAt: todayInput(),
  note: '',
});

const PURITY_OPTIONS = ['9K', '14K', '18K', '22K', '24K'];

const PURITY_FACTORS: Record<string, number> = {
  '9K': 9 / 24,
  '14K': 14 / 24,
  '18K': 18 / 24,
  '22K': 22 / 24,
  '24K': 24 / 24,
};

export const GoldSalePage: React.FC = () => {
  const { user, setUser } = useAuthStore();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<any>(null);
  const [repairs, setRepairs] = useState<any[]>([]);
  const [submitted, setSubmitted] = useState<any>(null);
  const [includeRepair, setIncludeRepair] = useState(false);
  const [repairLookupDone, setRepairLookupDone] = useState(false);

  const [formData, setFormData] = useState({
    staffName: user?.staffName || user?.name || '',
    cellNumber: '',
    customerName: '',
    goldWeight: '' as string,
    purity: '22K',
    marketRate: '' as string,
    vaPercent: '' as string,
    stoneCharges: '' as string,
    diamondCharges: '' as string,
    gemstoneCharges: '' as string,
    selectedRepairId: '' as string,
    repairFee: '' as string,
    paymentMethod: 'CASH',
    notes: '',
  });

  // Manual Paid / Unpaid payment & advance entries (staff typed in by hand).
  const [localPayments, setLocalPayments] = useState<LocalPayment[]>([]);
  const [paymentDraft, setPaymentDraft] = useState<LocalPayment>(emptyPayment());
  const [editingPaymentIdx, setEditingPaymentIdx] = useState<number | null>(null);
  const [paymentError, setPaymentError] = useState('');

  const update = (field: string, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const purityFactor = PURITY_FACTORS[formData.purity] || 1;
  const goldPrice = Number(formData.goldWeight) * Number(formData.marketRate) * purityFactor || 0;
  const vaPercentNum = Number(formData.vaPercent) || 0;
  const vaAmount = goldPrice * (vaPercentNum / 100);
  const stoneChargesNum = Number(formData.stoneCharges) || 0;
  const diamondChargesNum = Number(formData.diamondCharges) || 0;
  const gemstoneChargesNum = Number(formData.gemstoneCharges) || 0;
  const repairFeeNum = Number(formData.repairFee) || 0;
  const subtotal = goldPrice + vaAmount + stoneChargesNum + diamondChargesNum + gemstoneChargesNum + repairFeeNum;
  const tax = subtotal * 0.03;
  const grandTotal = subtotal + tax;

  // Manual payments roll-up
  const localPaid = localPayments.filter(p => p.status === 'PAID').reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const localAdvance = localPayments.filter(p => p.status === 'PAID' && p.kind === 'ADVANCE').reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balanceToPay = Math.max(0, grandTotal - localPaid);

  const savePaymentDraft = () => {
    if (!paymentDraft.amount || !(Number(paymentDraft.amount) > 0)) {
      setPaymentError('Enter an amount greater than zero');
      return;
    }
    setPaymentError('');
    if (editingPaymentIdx !== null) {
      setLocalPayments(prev => prev.map((p, i) => (i === editingPaymentIdx ? { ...paymentDraft } : p)));
      setEditingPaymentIdx(null);
    } else {
      setLocalPayments(prev => [...prev, { ...paymentDraft }]);
    }
    setPaymentDraft(emptyPayment());
  };

  const editLocalPayment = (idx: number) => {
    setPaymentDraft({ ...localPayments[idx] });
    setEditingPaymentIdx(idx);
    setPaymentError('');
  };

  const removeLocalPayment = (idx: number) => {
    setLocalPayments(prev => prev.filter((_, i) => i !== idx));
    if (editingPaymentIdx === idx) {
      setEditingPaymentIdx(null);
      setPaymentDraft(emptyPayment());
    }
  };

  const STORE_NAME = user?.name || 'Apni Jewellers';
  const STORE_PHONE = user?.phoneNumber || '+919999999999';
  const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${STORE_NAME}
ORG:${STORE_NAME}
TEL;TYPE=WORK,VOICE:${STORE_PHONE}
TEL;TYPE=WHATSAPP:${STORE_PHONE}
END:VCARD`;
  const whatsappLink = (phone: string) => `https://wa.me/${phone.replace(/[^0-9]/g, '')}`;
  const statusCheckUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?invoice=${submitted?.invoiceNumber || ''}`;
  const qrData = submitted ? `${vCardData}\n\nInvoice: ${submitted.invoiceNumber}\nCheck status: ${statusCheckUrl}` : vCardData;

  const handlePhoneCheck = async () => {
    if (!formData.cellNumber) { alert('Please enter cell number'); return; }
    setLoading(true);
    try {
      const res = await customerService.checkCustomer(formData.cellNumber);
      if (res.data.exists) {
        const c = res.data.customer;
        setCustomer(c);
        setFormData(prev => ({
          ...prev,
          customerName: c.customerName || prev.customerName,
          cellNumber: c.cellNumber || prev.cellNumber,
        }));
      } else {
        setCustomer(null);
      }
      setStep(2);
    } catch (error) {
      alert('Error checking customer');
      setCustomer(null);
    } finally { setLoading(false); }
  };

  const loadCustomerRepairs = async (customerId: string) => {
    if (repairLookupDone) return;
    try {
      const res = await repairService.getAll(customerId);
      const openRepairs = (res.data.repairs || []).filter(
        (r: any) => !['DELIVERED', 'COMPLETED', 'CANCELLED'].includes(r.repairStatus)
      );
      setRepairs(openRepairs);
      setRepairLookupDone(true);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };


  const handleRepairSelect = (repairId: string) => {
    const repair = repairs.find(r => r.id === repairId);
    const fee = repair?.finalCost || repair?.estimatedCost || 0;
    update('selectedRepairId', repairId);
    update('repairFee', String(fee));
  };

  const toggleIncludeRepair = async () => {
    const newVal = !includeRepair;
    setIncludeRepair(newVal);
    if (newVal && customer?.id && !repairLookupDone) {
      await loadCustomerRepairs(customer.id);
    }
    if (!newVal) {
      update('selectedRepairId', '');
      update('repairFee', '');
    }
  };

  const generateInvoiceNumber = () => {
    const ts = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `INV-${ts}${rand}`;
  };

  const handleSubmit = async () => {
    if (!formData.cellNumber) {
      alert('Customer phone number is required');
      return;
    }
    if (!customer && !formData.customerName) {
      alert('Please enter customer name');
      return;
    }
    if (!formData.goldWeight || !formData.marketRate) {
      alert('Please enter gold weight and market rate');
      return;
    }
    if (includeRepair && !formData.repairFee) {
      alert('Please enter repair fee');
      return;
    }
    setLoading(true);
    try {
      let customerId = customer?.id;

      if (!customerId) {
        const custRes = await customerService.createOrUpdate({
          customerName: formData.customerName,
          cellNumber: formData.cellNumber,
          sourceType: 'NEW',
        });
        customerId = custRes.data.customer?.id;
      }

      if (!customerId) {
        throw new Error('Could not resolve customer');
      }

      const items: any[] = [
        {
          itemDescription: `Gold - ${formData.purity}`,
          category: 'Gold',
          quantity: 1,
          unitPrice: Number(formData.marketRate),
          totalPrice: goldPrice,
          weight: Number(formData.goldWeight),
          purity: formData.purity,
          rate: Number(formData.marketRate),
          purityFactor,
        },
      ];

      if (vaAmount > 0) {
        items.push({
          itemDescription: `Making Charges / VA (${formData.vaPercent}%)`,
          category: 'Gold',
          quantity: 1,
          unitPrice: vaAmount,
          totalPrice: vaAmount,
        });
      }

      if (stoneChargesNum > 0) {
        items.push({
          itemDescription: 'Stone Charges',
          category: 'Stone',
          quantity: 1,
          unitPrice: stoneChargesNum,
          totalPrice: stoneChargesNum,
        });
      }

      if (diamondChargesNum > 0) {
        items.push({
          itemDescription: 'Diamond Charges',
          category: 'Diamond',
          quantity: 1,
          unitPrice: diamondChargesNum,
          totalPrice: diamondChargesNum,
        });
      }

      if (gemstoneChargesNum > 0) {
        items.push({
          itemDescription: 'Gemstone Charges',
          category: 'Gemstone',
          quantity: 1,
          unitPrice: gemstoneChargesNum,
          totalPrice: gemstoneChargesNum,
        });
      }

      if (repairFeeNum > 0) {
        items.push({
          itemDescription: `Repair Fee${formData.selectedRepairId ? ' (Ticket linked)' : ''}`,
          category: 'Repair',
          quantity: 1,
          unitPrice: repairFeeNum,
          totalPrice: repairFeeNum,
        });
      }

      const invoiceNumber = generateInvoiceNumber();
      let notes = formData.notes || '';
      if (formData.selectedRepairId) {
        notes = `Linked Repair ID: ${formData.selectedRepairId}${notes ? ' | ' + notes : ''}`;
      }
      if (formData.staffName) {
        notes = `Staff: ${formData.staffName}${notes ? ' | ' + notes : ''}`;
      }

      const orderRes = await orderService.create({
        customerId,
        salesPersonName: formData.staffName || user?.name || 'Staff',
        invoiceNumber,
        items,
        totalAmount: grandTotal,
        tax,
        discount: 0,
        cashAdvance: localAdvance,
        paymentMethod: formData.paymentMethod,
        paymentStatus: localPaid <= 0 ? 'PENDING' : (balanceToPay <= 0.005 ? 'PAID' : 'PARTIAL'),
        notes,
      });

      const createdOrder = orderRes.data?.order;
      if (createdOrder?.id && localPayments.length > 0) {
        for (const p of localPayments) {
          try {
            await orderService.addPayment(createdOrder.id, {
              amount: Number(p.amount),
              kind: p.kind,
              status: p.status,
              method: p.method,
              paidAt: p.paidAt,
              note: p.note || null,
            });
          } catch (err) {
            console.error('Failed to save payment entry', err);
          }
        }
      }

      if (formData.selectedRepairId) {
        await repairService.updateStage(formData.selectedRepairId, {
          stage: 'DELIVERY',
          notes: 'Completed via Gold Sale',
          finalCost: repairFeeNum,
        });
      }

      if (formData.staffName && user) {
        setUser({ ...user, staffName: formData.staffName });
      }

      setSubmitted({ ...orderRes.data.order, invoiceNumber });
      setStep(4);
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Unknown error';
      const details = error.response?.data ? JSON.stringify(error.response.data) : '';
      alert(`Error: ${msg}${details ? '\n\nDetails: ' + details : ''}`);
      console.error('Submit error:', error);
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setStep(1);
    setCustomer(null);
    setRepairs([]);
    setSubmitted(null);
    setIncludeRepair(false);
    setRepairLookupDone(false);
    setLocalPayments([]);
    setPaymentDraft(emptyPayment());
    setEditingPaymentIdx(null);
    setPaymentError('');
    setFormData({
      staffName: user?.staffName || user?.name || '',
      cellNumber: '', customerName: '', goldWeight: '', purity: '22K',
      marketRate: '', vaPercent: '', stoneCharges: '', diamondCharges: '', gemstoneCharges: '',
      selectedRepairId: '', repairFee: '', paymentMethod: 'CASH', notes: '',
    });
  };

  return (
    <div className='min-h-screen bg-slate-50 p-4 md:p-8 text-slate-900'>
      <div className='max-w-3xl mx-auto'>

        {/* Step 1: Staff & Customer Details */}
        {step === 1 && (
          <GlassPanel className='p-8'>
            <h2 className='text-3xl font-bold text-slate-900 mb-2'>Gold Sale</h2>
            <p className='text-slate-500 mb-6'>Record staff and customer details.</p>

            <div className='mb-6'>
              <label className='block text-slate-700 font-semibold mb-2 flex items-center gap-2'>
                <FaUser className='text-slate-400' /> Staff Name
              </label>
              <GlassInput
                value={formData.staffName}
                onChange={e => update('staffName', e.target.value)}
                placeholder='Staff handling the sale'
              />
            </div>

            <div className='mb-4'>
              <label className='block text-slate-700 font-semibold mb-2'>Customer Phone Number *</label>
              <div className='flex gap-2'>
                <GlassInput
                  type='tel' placeholder='+91...' value={formData.cellNumber}
                  onChange={e => update('cellNumber', e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePhoneCheck()}
                />
                <GlassButton onClick={handlePhoneCheck} disabled={loading} variant='primary'>
                  {loading ? '...' : 'Search'}
                </GlassButton>
              </div>
              <p className='text-xs text-slate-500 mt-1'>Search existing or enter phone for new customer</p>
            </div>

            <div className='mb-4'>
              <label className='block text-slate-700 font-semibold mb-2'>Customer Name {!customer ? '*' : ''}</label>
              <GlassInput
                value={formData.customerName}
                onChange={e => update('customerName', e.target.value)}
                placeholder='Full name'
              />
            </div>

            {customer && (
              <GlassCard className='mb-4 bg-blue-50 border-blue-200 flex items-center justify-between'>
                <div>
                  <p className='text-blue-800 font-medium'>{customer.customerName}</p>
                  <p className='text-blue-600 text-sm'>{customer.cellNumber}</p>
                </div>
                <FaCheck className='text-blue-500' />
              </GlassCard>
            )}

            <GlassButton
              variant='primary' className='w-full mt-4'
              disabled={loading}
              onClick={() => {
                if (!formData.staffName) {
                  alert('Please enter staff name');
                  return;
                }
                if (!formData.cellNumber) {
                  alert('Please enter customer phone number');
                  return;
                }
                if (!customer && !formData.customerName) {
                  alert('Please enter customer name');
                  return;
                }
                if (!customer) {
                  handlePhoneCheck();
                } else {
                  setStep(2);
                }
              }}
            >
              {loading ? 'Checking...' : 'Next - Price the Gold'}
            </GlassButton>
          </GlassPanel>
        )}

        {/* Step 2: Gold Pricing */}
        {step === 2 && (
          <GlassPanel className='p-8'>
            <h2 className='text-2xl font-bold text-slate-900 mb-6'>Price the Gold</h2>

            <GlassCard className='mb-6 bg-green-50 border-green-200'>
              <p className='text-green-800 font-semibold'>{customer?.customerName || formData.customerName}</p>
              <p className='text-green-600 text-sm'>{customer?.cellNumber || formData.cellNumber}</p>
            </GlassCard>

            {/* Gold Weight & Purity */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              <div>
                <label className='block text-slate-700 font-semibold mb-1 text-sm flex items-center gap-2'>
                  <FaWeight className='text-amber-500' /> Gold Weight (g)
                </label>
                <GlassInput
                  type='number' step='0.01' min='0'
                  value={formData.goldWeight}
                  onChange={e => update('goldWeight', e.target.value)}
                  placeholder='e.g., 10.5'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-1 text-sm'>Purity</label>
                <GlassSelect value={formData.purity} onChange={e => update('purity', e.target.value)}>
                  {PURITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </GlassSelect>
                <p className='text-xs text-slate-500 mt-1'>{(purityFactor * 100).toFixed(2)}% pure gold</p>
              </div>
            </div>

            {/* Market Rate */}
            <div className='mb-4'>
              <label className='block text-slate-700 font-semibold mb-1 text-sm flex items-center gap-2'>
                <FaRupeeSign className='text-amber-500' /> 24 Carat Market Gold Rate (₹/g)
              </label>
              <GlassInput
                type='number' step='0.01' min='0'
                value={formData.marketRate}
                onChange={e => update('marketRate', e.target.value)}
                placeholder='e.g., 6500'
              />
              <p className='text-xs text-slate-500 mt-1'>Today's 24 carat (pure gold) market rate per gram. Lower karat gold is priced automatically from this rate.</p>
            </div>

            {/* VA (%) */}
            <div className='mb-4'>
              <label className='block text-slate-700 font-semibold mb-1 text-sm flex items-center gap-2'>
                <FaHammer className='text-amber-500' /> VA / Making Charges (%)
              </label>
              <GlassInput
                type='number' step='0.01' min='0' max='100'
                value={formData.vaPercent}
                onChange={e => update('vaPercent', e.target.value)}
                placeholder='e.g., 12'
              />
              {vaAmount > 0 && (
                <p className='text-xs text-green-700 mt-1'>VA Amount: ₹{vaAmount.toFixed(2)}</p>
              )}
            </div>

            {/* Stone / Diamond / Gemstone Charges */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
              <div>
                <label className='block text-slate-700 font-semibold mb-1 text-sm'>Stone Charges (₹)</label>
                <GlassInput
                  type='number' step='0.01' min='0'
                  value={formData.stoneCharges}
                  onChange={e => update('stoneCharges', e.target.value)}
                  placeholder='0.00'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-1 text-sm flex items-center gap-1'>
                  <FaStar className='text-blue-400' size={12} /> Diamond Charges (₹)
                </label>
                <GlassInput
                  type='number' step='0.01' min='0'
                  value={formData.diamondCharges}
                  onChange={e => update('diamondCharges', e.target.value)}
                  placeholder='0.00'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-1 text-sm flex items-center gap-1'>
                  <FaGem className='text-purple-400' size={12} /> Gemstone Charges (₹)
                </label>
                <GlassInput
                  type='number' step='0.01' min='0'
                  value={formData.gemstoneCharges}
                  onChange={e => update('gemstoneCharges', e.target.value)}
                  placeholder='0.00'
                />
              </div>
            </div>

            <GlassButton
              variant='primary' className='w-full mb-3'
              onClick={() => setStep(3)}
              disabled={!formData.goldWeight || !formData.marketRate}
            >
              Next - Review & Checkout
            </GlassButton>

            <GlassButton variant='secondary' className='w-full' onClick={() => setStep(1)}>
              Back
            </GlassButton>
          </GlassPanel>
        )}

        {/* Step 3: Review & Checkout (with repair option) */}
        {step === 3 && (
          <GlassPanel className='p-8'>
            <h2 className='text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2'>
              <FaBoxOpen className='text-slate-500' /> Review & Checkout
            </h2>

            {/* Summary */}
            <GlassCard className='mb-6 bg-slate-50 border-slate-200'>
              <div className='space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-slate-600'>
                    Gold ({formData.goldWeight}g × ₹{formData.marketRate} × {formData.purity} {(purityFactor * 100).toFixed(1)}%)
                  </span>
                  <span className='font-semibold flex items-center gap-1'>
                    <FaRupeeSign className='text-amber-500' size={14} />{goldPrice.toFixed(2)}
                  </span>
                </div>
                {vaAmount > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600 flex items-center gap-1'>
                      <FaHammer className='text-slate-400' size={14} /> VA / Making ({formData.vaPercent}%)
                    </span>
                    <span className='font-semibold flex items-center gap-1'>
                      <FaRupeeSign className='text-amber-500' size={14} />{vaAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {stoneChargesNum > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600'>Stone Charges</span>
                    <span className='font-semibold flex items-center gap-1'>
                      <FaRupeeSign className='text-amber-500' size={14} />{stoneChargesNum.toFixed(2)}
                    </span>
                  </div>
                )}
                {diamondChargesNum > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600 flex items-center gap-1'>
                      <FaStar className='text-blue-400' size={12} /> Diamond Charges
                    </span>
                    <span className='font-semibold flex items-center gap-1'>
                      <FaRupeeSign className='text-amber-500' size={14} />{diamondChargesNum.toFixed(2)}
                    </span>
                  </div>
                )}
                {gemstoneChargesNum > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600 flex items-center gap-1'>
                      <FaGem className='text-purple-400' size={12} /> Gemstone Charges
                    </span>
                    <span className='font-semibold flex items-center gap-1'>
                      <FaRupeeSign className='text-amber-500' size={14} />{gemstoneChargesNum.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Do you have any repair? */}
            <GlassCard className='mb-6 border-amber-200'>
              <label className='flex items-center gap-3 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={includeRepair}
                  onChange={toggleIncludeRepair}
                  className='w-5 h-5 accent-slate-900'
                />
                <span className='font-semibold text-slate-900 flex items-center gap-2'>
                  <FaWrench className='text-amber-500' /> Do you have any repair to include?
                </span>
              </label>

              {includeRepair && (
                <div className='mt-4 pt-4 border-t border-slate-200'>
                  {repairs.length === 0 && repairLookupDone && (
                    <p className='text-slate-500 text-sm mb-3'>No open repairs found for this customer. Enter fee manually.</p>
                  )}

                  {repairs.length > 0 && (
                    <div className='space-y-2 mb-3'>
                      {repairs.map((repair: any) => (
                        <div
                          key={repair.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            formData.selectedRepairId === repair.id
                              ? 'border-slate-900 bg-slate-50'
                              : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          onClick={() => handleRepairSelect(repair.id)}
                        >
                          <div className='flex justify-between items-center'>
                            <div>
                              <p className='font-semibold text-sm text-slate-900'>{repair.repairTicket}</p>
                              <p className='text-xs text-slate-600'>{repair.itemDescription}</p>
                            </div>
                            <p className='font-bold text-sm flex items-center gap-1'>
                              <FaRupeeSign className='text-amber-500' size={12} />
                              {repair.finalCost || repair.estimatedCost || 0}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!repairLookupDone && customer?.id && (
                    <GlassButton
                      variant='secondary' className='w-full mb-3 text-sm'
                      onClick={() => loadCustomerRepairs(customer.id)}
                    >
                      <FaWrench /> Load My Repairs
                    </GlassButton>
                  )}

                  <div>
                    <label className='block text-slate-700 font-semibold mb-1 text-sm'>Repair Fee (₹)</label>
                    <GlassInput
                      type='number' step='0.01' min='0'
                      value={formData.repairFee}
                      onChange={e => update('repairFee', e.target.value)}
                      placeholder='Enter repair fee'
                    />
                  </div>
                </div>
              )}
            </GlassCard>

            {/* Total */}
            <GlassCard className='mb-6 border-slate-200'>
              <div className='space-y-2'>
                <div className='flex justify-between text-base'>
                  <span className='text-slate-600'>Subtotal</span>
                  <span className='font-bold flex items-center gap-1'>
                    <FaRupeeSign className='text-amber-500' size={16} />{subtotal.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between text-base'>
                  <span className='text-slate-600 flex items-center gap-1'>
                    <FaPercent className='text-blue-500' size={14} /> GST (3%)
                  </span>
                  <span className='font-semibold flex items-center gap-1'>
                    <FaRupeeSign className='text-amber-500' size={14} />{tax.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between text-xl font-bold text-slate-900 pt-2 border-t-2 border-slate-900'>
                  <span>Grand Total</span>
                  <span className='flex items-center gap-1'>
                    <FaRupeeSign className='text-amber-500' />{grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </GlassCard>

            {/* Manual payments / advance — Paid & Unpaid entries with history */}
            <GlassCard className='mb-6 border-emerald-200'>
              <h3 className='font-bold text-slate-900 mb-1 flex items-center gap-2'>
                <FaWallet className='text-emerald-600' /> Payments &amp; Advance
                <span className='text-xs font-normal text-slate-400'>(enter by hand)</span>
              </h3>
              <p className='text-xs text-slate-500 mb-4'>
                Add the advance or amount actually received. Mark each entry Paid or Unpaid. You can edit or delete any entry here — and later from the order page.
              </p>

              <div className='mb-4'>
                <label className='block text-slate-700 font-semibold mb-2 text-sm'>Default Payment Method</label>
                <GlassSelect
                  value={formData.paymentMethod}
                  onChange={e => update('paymentMethod', e.target.value)}
                >
                  <option value='CASH'>Cash</option>
                  <option value='UPI'>UPI</option>
                  <option value='CARD'>Card</option>
                  <option value='CHEQUE'>Cheque</option>
                  <option value='ONLINE'>Online Transfer</option>
                </GlassSelect>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4'>
                <div className='rounded-xl bg-slate-50 border border-slate-200 p-3'>
                  <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-600'>Total Amount</p>
                  <p className='text-lg font-bold text-slate-800'>₹{grandTotal.toFixed(2)}</p>
                </div>
                <div className='rounded-xl bg-emerald-50 border border-emerald-100 p-3'>
                  <p className='text-[11px] font-semibold uppercase tracking-wide text-emerald-700'>Advance / Paid</p>
                  <p className='text-lg font-bold text-emerald-700'>₹{localPaid.toFixed(2)}</p>
                </div>
                <div className='rounded-xl bg-red-50 border border-red-100 p-3'>
                  <p className='text-[11px] font-semibold uppercase tracking-wide text-red-700'>Balance To Pay</p>
                  <p className='text-lg font-bold text-red-700'>₹{balanceToPay.toFixed(2)}</p>
                </div>
              </div>

              {paymentError && (
                <div className='mb-3 text-sm rounded-xl px-3 py-2 bg-red-50 border border-red-200 text-red-700'>
                  {paymentError}
                </div>
              )}

              {/* Entry form */}
              <div className='rounded-xl border border-slate-200 bg-white p-3 mb-3'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-3 mb-3'>
                  <div>
                    <label className='block text-slate-600 font-semibold mb-1 text-xs'>Advance / Amount Given (₹) *</label>
                    <GlassInput
                      type='number' step='0.01' min='0'
                      value={paymentDraft.amount}
                      onChange={e => setPaymentDraft({ ...paymentDraft, amount: e.target.value })}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <label className='block text-slate-600 font-semibold mb-1 text-xs'>Type</label>
                    <GlassSelect
                      value={paymentDraft.kind}
                      onChange={e => setPaymentDraft({ ...paymentDraft, kind: e.target.value })}
                    >
                      <option value='ADVANCE'>Advance</option>
                      <option value='PAYMENT'>Balance Payment</option>
                      <option value='FINAL'>Final / Full</option>
                    </GlassSelect>
                  </div>
                  <div>
                    <label className='block text-slate-600 font-semibold mb-1 text-xs'>Method</label>
                    <GlassSelect
                      value={paymentDraft.method}
                      onChange={e => setPaymentDraft({ ...paymentDraft, method: e.target.value })}
                    >
                      <option value='CASH'>Cash</option>
                      <option value='UPI'>UPI</option>
                      <option value='CARD'>Card</option>
                      <option value='CHEQUE'>Cheque</option>
                      <option value='ONLINE'>Online Transfer</option>
                      <option value='BANK_TRANSFER'>Bank Transfer</option>
                      <option value='OTHER'>Other</option>
                    </GlassSelect>
                  </div>
                  <div>
                    <label className='block text-slate-600 font-semibold mb-1 text-xs'>Date</label>
                    <GlassInput
                      type='date'
                      value={paymentDraft.paidAt}
                      onChange={e => setPaymentDraft({ ...paymentDraft, paidAt: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-slate-600 font-semibold mb-1 text-xs'>Note</label>
                    <GlassInput
                      value={paymentDraft.note}
                      onChange={e => setPaymentDraft({ ...paymentDraft, note: e.target.value })}
                      placeholder='Optional'
                    />
                  </div>
                </div>
                <div className='flex gap-2'>
                  <GlassButton variant='primary' className='flex-1' onClick={savePaymentDraft}>
                    {editingPaymentIdx !== null
                      ? <><FaEdit className='mr-1' /> Update Entry</>
                      : <><FaPlus className='mr-1' /> Add Entry</>}
                  </GlassButton>
                  {editingPaymentIdx !== null && (
                    <GlassButton
                      variant='secondary'
                      onClick={() => { setEditingPaymentIdx(null); setPaymentDraft(emptyPayment()); setPaymentError(''); }}
                    >
                      Cancel
                    </GlassButton>
                  )}
                </div>
              </div>

              {/* History */}
              <div className='flex items-center gap-2 mb-2 text-slate-500'>
                <FaHistory size={12} />
                <span className='text-xs font-semibold uppercase tracking-wide'>Payment history</span>
              </div>

              {localPayments.length === 0 ? (
                <p className='text-sm text-slate-400 py-3 text-center'>
                  No entries yet. Add the advance or payment you have taken.
                </p>
              ) : (
                <div className='space-y-2'>
                  {localPayments.map((p, idx) => (
                    <div key={idx} className='flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3'>
                      <div className='min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <span className='font-bold text-slate-900'>₹{(Number(p.amount) || 0).toFixed(2)}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.kind === 'ADVANCE' ? 'bg-blue-100 text-blue-700' : p.kind === 'FINAL' ? 'bg-purple-100 text-purple-700' : 'bg-slate-200 text-slate-600'}`}>
                            {p.kind}
                          </span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {p.status}
                          </span>
                        </div>
                        <p className='text-xs text-slate-500 mt-1'>
                          {p.paidAt || '-'}{p.method ? ` · ${p.method}` : ''}{p.note ? ` · ${p.note}` : ''}
                        </p>
                      </div>
                      <div className='flex items-center gap-1 shrink-0'>
                        <button
                          onClick={() => editLocalPayment(idx)}
                          className='p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                          title='Edit entry'
                        >
                          <FaEdit size={13} />
                        </button>
                        <button
                          onClick={() => removeLocalPayment(idx)}
                          className='p-2 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700'
                          title='Delete entry'
                        >
                          <FaTrash size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            <div className='mb-6'>
              <label className='block text-slate-700 font-semibold mb-2'>Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={e => update('notes', e.target.value)}
                placeholder='Additional notes...'
                className='w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all'
                rows={2}
              />
            </div>

            <div className='flex gap-3'>
              <GlassButton variant='secondary' onClick={() => setStep(2)} className='flex-1'>
                Back
              </GlassButton>
              <GlassButton variant='primary' className='flex-1' onClick={handleSubmit} disabled={loading}>
                {loading ? 'Processing...' : `Confirm ₹${grandTotal.toFixed(2)}`}
              </GlassButton>
            </div>
            {localPayments.length > 0 && (
              <p className='text-xs text-slate-500 mt-3 text-center'>
                Paid ₹{localPaid.toFixed(2)} · Balance ₹{balanceToPay.toFixed(2)}
              </p>
            )}
          </GlassPanel>
        )}

        {/* Step 4: Success */}
        {step === 4 && submitted && (
          <GlassPanel className='p-8 text-center'>
            <div className='text-5xl mb-4'>
              <FaCheck className='text-green-500 mx-auto' />
            </div>
            <h2 className='text-3xl font-bold text-slate-900 mb-2'>Sale Complete!</h2>
            <p className='text-slate-500 mb-6'>Bill has been generated successfully.</p>

            <GlassCard className='mb-4 bg-green-50 border-green-200 text-left'>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Invoice</span>
                  <span className='font-bold text-slate-900'>{submitted.invoiceNumber}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Staff</span>
                  <span className='font-semibold text-slate-900'>{formData.staffName}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Customer</span>
                  <span className='font-semibold text-slate-900'>{customer?.customerName || formData.customerName}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Gold</span>
                  <span className='font-semibold text-slate-900'>{formData.goldWeight}g ({formData.purity})</span>
                </div>
                {vaAmount > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-slate-600'>VA ({formData.vaPercent}%)</span>
                    <span className='font-semibold text-slate-900'>₹{vaAmount.toFixed(2)}</span>
                  </div>
                )}
                {stoneChargesNum > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-slate-600'>Stone</span>
                    <span className='font-semibold text-slate-900'>₹{stoneChargesNum.toFixed(2)}</span>
                  </div>
                )}
                {diamondChargesNum > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-slate-600'>Diamond</span>
                    <span className='font-semibold text-slate-900'>₹{diamondChargesNum.toFixed(2)}</span>
                  </div>
                )}
                {gemstoneChargesNum > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-slate-600'>Gemstone</span>
                    <span className='font-semibold text-slate-900'>₹{gemstoneChargesNum.toFixed(2)}</span>
                  </div>
                )}
                {repairFeeNum > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-slate-600'>Repair Fee</span>
                    <span className='font-semibold text-slate-900'>₹{repairFeeNum.toFixed(2)}</span>
                  </div>
                )}
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Total</span>
                  <span className='font-bold text-slate-900 flex items-center gap-1'>
                    <FaRupeeSign className='text-amber-500' size={14} />{grandTotal.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-600'>Payment</span>
                  <span className='font-semibold text-green-700'>{formData.paymentMethod}</span>
                </div>
              </div>
            </GlassCard>

            {/* Manual payments recorded — full history, editable */}
            <div className='text-left mb-6'>
              <PaymentsPanel kind='order' recordId={submitted.id} totalAmount={grandTotal} />
            </div>

            {/* QR Code - Save Store Contact & Check Status on WhatsApp */}
            <GlassCard className='mb-6 bg-blue-50 border-blue-200'>
              <h3 className='font-bold text-slate-900 mb-3 text-center'>Save our Store Contact</h3>
              <p className='text-sm text-slate-600 mb-4 text-center'>
                Scan this QR code to save {STORE_NAME}&apos;s number and check your invoice status on WhatsApp.
              </p>
              <div className='flex justify-center mb-4'>
                <QrCode
                  value={qrData}
                  size={192}
                  fileName={submitted ? `invoice-${submitted.invoiceNumber}-qr` : 'store-contact-qr'}
                />
              </div>
              <a
                href={whatsappLink(STORE_PHONE)}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all'
              >
                <FaWhatsapp size={20} /> Chat on WhatsApp
              </a>
            </GlassCard>

            <GlassButton variant='primary' className='w-full' onClick={resetForm}>
              New Sale
            </GlassButton>
          </GlassPanel>
        )}
      </div>
    </div>
  );
};

export default GoldSalePage;
